import React from "react";
import {
  axiosInstance,
  ColDef,
  DEFAULT_PAGINATION,
  IServerSideGetRowsParams,
  PaginatedApiResponse
} from "@fides/fides-react-components";
import { AxiosRequestConfig } from "axios";
import { TFunction } from "i18next";

/**
 * Field names in the grid's columnDef could vary from the query parameter names
 * expected by the backend. Ag-grid's filterModel and sortModel object uses the
 * columnDef filter key which we need to convert.
 *
 * The conversion in done by a `columnMapping` object.
 *
 * Example:
 *
 * columnDefs = [
 *   ...,
 *   {
 *     field: 'masterAccount.account.name',
 *   },
 *   ...
 * ]
 *
 * const columnMapping = {
 *   'masterAccount.account.name': 'masterAccountName',
 * };
 */
export const mapFilterModel = (filterModel: any, t: TFunction, columnMapping?: ColDef[]) => {
  return Object.entries(filterModel).reduce((acc, cur, idx) => {
    const [key, value]: [any, any] = cur;
    const queryKey = (columnMapping && columnMapping[key]) || key;

    if (value.filterType === 'set') {
      // Special filter type Select + Boolean + Custom values mapping
      if (key === 'active') {
        acc[queryKey] = value?.values.map((text: string) => value?.values[0] === t('generic.active') ? 'true' : 'false');
      } else {
        acc[queryKey] = value.values;
      }
    } else if (value.filterType !== 'text') {
      acc[queryKey + 'Range'] = value;

      if (value.filterType === 'date') {
        // Extracting only the date from the date + time fields
        value.dateFrom = value.dateFrom ? value.dateFrom.substring(0, 10) : null;
        value.dateTo = value.dateTo ? value.dateTo.substring(0, 10) : null;
        value.zoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
      }
    } else {
      acc[queryKey] = (value as any).filter;
    }

    return acc;
  }, {} as any);
}

export function useDatasource<T>(
  config: AxiosRequestConfig,
  t: TFunction,
  columnMapping?: ColDef[]
) {
  return React.useMemo(
    () => createDatasource<T>(config, t, columnMapping),
    [config, columnMapping, t]
  );
}

export function createDatasource<T>(
  originalConfig: AxiosRequestConfig,
  t: TFunction,
  columnMapping?: ColDef[]
) {
  return {
    getRows: async function (params: IServerSideGetRowsParams) {
      const startRow = params.request.startRow || 0;
      const endRow = params.request.endRow || DEFAULT_PAGINATION;

      const filterParams = params.request.filterModel
        ? mapFilterModel(params.request.filterModel, t, columnMapping)
        : undefined;

      const gridQueryParams: AxiosRequestConfig['params'] = {};

      if (filterParams && Object.keys(filterParams).length > 0) {
        if (originalConfig.params?.filter) {
          gridQueryParams['filter'] = {
            ...originalConfig.params.filter,
            ...filterParams,
          };
        } else {
          gridQueryParams['filter'] = filterParams;
        }
      }

      // Multicolumn sorting
      const { sortModel } = params?.request;
      const getSortColumnForColumnId = (columnId: any) => (columnMapping && columnMapping[columnId]) || columnId;
      const sortingParams = sortModel && sortModel.map((sortModelItem: { sort: string, colId: string }) => `${getSortColumnForColumnId(sortModelItem.colId)},${sortModelItem.sort}`);

      gridQueryParams['size'] = endRow - startRow;
      gridQueryParams['page'] = Math.ceil(endRow / gridQueryParams['size']) - 1;
      gridQueryParams['sort'] = sortingParams;

      const requestConfig: AxiosRequestConfig = { ...originalConfig };
      requestConfig.params = {
        ...originalConfig.params,
        ...gridQueryParams,
      };

      let response;
      try {
        response = await axiosInstance.request<PaginatedApiResponse<T>>(
          requestConfig
        );
      } catch {
        params.fail();
      }

      if (response && response.data) {
        params.success({
          rowData: response.data.content,
          rowCount: response.data.totalElements || 0
      });
      } else {
        // inform grid request failed
        params.fail();
      }
    },
  };
}